import { useEffect, useState } from 'react';

import { defaultEgsCategoryDraft, defaultEgsRoomDraft } from './egsModel';
import { EMPTY_COMPLETION, applyRoomToPending, buildInitialWizardState, findNextRoomIndex } from './egsWizardEngine';
import { EGS_CATS } from './fields';
import type {
  EgsCategoryCompletion,
  EgsCategoryMeta,
  EgsDraft,
  EgsDraftInput,
  EgsGuestCategoryDraft,
  EgsGuestCategoryKey,
  EgsRoomDraft,
} from './types';

interface HistorySnapshot {
  completed: Record<string, EgsCategoryCompletion>;
  pendingLength: number;
  roomIndex: number;
}

interface Props {
  audToSourceCurrencyRate: Record<string, number | undefined>;
  currency: string;
  editingDraft?: EgsDraft;
  isFxRateLoading: boolean;
  onClose: () => void;
  onCommit: (records: Array<EgsDraftInput>) => void;
  open: boolean;
  roomTypes: Array<string>;
}

export interface EgsWizardModalState {
  alreadySet: Array<EgsCategoryMeta>;
  draft: EgsRoomDraft;
  error: string | undefined;
  handleBack: () => void;
  handleClose: () => void;
  handleNext: () => void;
  hasHistory: boolean;
  hasMoreRoomsAfterThis: boolean;
  roomCompleted: EgsCategoryCompletion;
  roomIndex: number;
  roomName: string;
  toggleCategory: (key: EgsGuestCategoryKey) => void;
  updateCategory: (key: EgsGuestCategoryKey, changes: Partial<EgsGuestCategoryDraft>) => void;
  updateRoomDraft: (changes: Partial<EgsRoomDraft>) => void;
}

export function useEgsWizardModalState({
  audToSourceCurrencyRate,
  currency,
  editingDraft,
  isFxRateLoading,
  onClose,
  onCommit,
  open,
  roomTypes,
}: Props): EgsWizardModalState | null {
  const [roomIndex, setRoomIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, EgsRoomDraft>>({});
  const [completed, setCompleted] = useState<Record<string, EgsCategoryCompletion>>({});
  const [pending, setPending] = useState<Array<EgsDraftInput>>([]);
  const [history, setHistory] = useState<Array<HistorySnapshot>>([]);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      return;
    }

    const initial = buildInitialWizardState(editingDraft, roomTypes);
    setDrafts(initial.drafts);
    setCompleted(initial.completed);
    setRoomIndex(initial.roomIndex);
    setPending([]);
    setHistory([]);
    setError(undefined);
  }, [open, roomTypes, editingDraft]);

  if (!open || !roomTypes.length) {
    return null;
  }

  const roomName = roomTypes[roomIndex] ?? roomTypes[0];
  const draft = drafts[roomName] ?? defaultEgsRoomDraft();
  const roomCompleted = completed[roomName] ?? EMPTY_COMPLETION;
  const alreadySet = EGS_CATS.filter((meta) => roomCompleted[meta.key]);
  const hasMoreRoomsAfterThis = findNextRoomIndex(roomTypes, completed, roomIndex + 1) >= 0;

  const updateRoomDraft = (changes: Partial<EgsRoomDraft>) => {
    setDrafts((prev) => ({ ...prev, [roomName]: { ...(prev[roomName] ?? defaultEgsRoomDraft()), ...changes } }));
  };

  const updateCategory = (key: EgsGuestCategoryKey, changes: Partial<EgsGuestCategoryDraft>) => {
    setDrafts((prev) => {
      const roomDraft = prev[roomName] ?? defaultEgsRoomDraft();
      return {
        ...prev,
        [roomName]: { ...roomDraft, [key]: { ...(roomDraft[key] ?? defaultEgsCategoryDraft()), ...changes } },
      };
    });
  };

  const toggleCategory = (key: EgsGuestCategoryKey) => {
    if (roomCompleted[key]) {
      return;
    }
    const nextEnabled = !draft[key].enabled;
    // Seed the category's own scope from the section-level control on enable, so "Set these
    // EGS rules for" actually drives the generated records - the category radio still overrides.
    updateCategory(
      key,
      nextEnabled
        ? { enabled: true, applyMode: draft.globalApplyMode, applyRooms: draft.globalApplyRooms }
        : { enabled: false },
    );
  };

  const handleBack = () => {
    if (!history.length) {
      return;
    }
    const snapshot = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setPending((prev) => prev.slice(0, snapshot.pendingLength));
    setCompleted(snapshot.completed);
    setRoomIndex(snapshot.roomIndex);
    setError(undefined);
  };

  const handleClose = () => {
    setError(undefined);
    onClose();
  };

  const handleNext = () => {
    const result = applyRoomToPending({
      audToSourceCurrencyRate,
      completed,
      currency,
      draft,
      isFxRateLoading,
      roomCompleted,
      roomName,
      roomTypes,
    });

    if (result.error) {
      setError(result.error);
      return;
    }

    setError(undefined);
    setHistory((prev) => [...prev, { completed, pendingLength: pending.length, roomIndex }]);
    setCompleted(result.nextCompleted);

    const newPending = [...pending, ...result.newRecords];
    setPending(newPending);

    const nextIndex = findNextRoomIndex(roomTypes, result.nextCompleted, 0);
    if (nextIndex < 0) {
      onCommit(newPending);
      handleClose();
      return;
    }
    setRoomIndex(nextIndex);
  };

  return {
    alreadySet,
    draft,
    error,
    handleBack,
    handleClose,
    handleNext,
    hasHistory: history.length > 0,
    hasMoreRoomsAfterThis,
    roomCompleted,
    roomIndex,
    roomName,
    toggleCategory,
    updateCategory,
    updateRoomDraft,
  };
}
